/**
 * OBJ (Object / grid data) slice — fetches and holds the row data for SDUI grids.
 * Each row is an ObjResponse keyed by the grid's DTS, BA, and language context.
 *
 * fetchObjList returns both the data and the originating dtsId so the fulfilled
 * handler can target the correct grid when multiple fetches are in flight.
 * setObjList allows optimistic/manual updates without a round-trip.
 */
import objService, { ObjResponse } from '@/features/it-process/application-management/sdui/services/ObjService'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface ObjState {
    objList: ObjResponse[]
    loading: boolean
    error: string | null
}

const initialState: ObjState = {
    objList: [],
    loading: false,
    error: null,
}

interface FetchObjParams {
    dtsId: number
    languageId?: number
    baId?: number
}

export const fetchObjList = createAsyncThunk(
    'obj/fetchObjList',
    async (params: FetchObjParams, { rejectWithValue }) => {
        try {
            const response = await objService.getObjList(
                params.dtsId,
                params.baId,
                params.languageId,
            )
            return { data: response, dtsId: params.dtsId }
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch obj list',
            )
        }
    },
)

export const objSlice = createSlice({
    name: 'obj',
    initialState,
    reducers: {
        setObjList: (state, action: PayloadAction<ObjResponse[]>) => {
            state.objList = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchObjList.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchObjList.fulfilled, (state, action) => {
                state.loading = false
                state.objList = action.payload.data
            })
            .addCase(fetchObjList.rejected, (state, action) => {
                state.loading = false
                state.error = (action.payload as string) ?? 'Failed to fetch obj list'
            })
    },
})

export const { setObjList } = objSlice.actions

export const selectObjList = (state: { obj: ObjState }): ObjResponse[] =>
    state.obj.objList
export const selectObjLoading = (state: { obj: ObjState }): boolean =>
    state.obj.loading
export const selectObjError = (state: { obj: ObjState }): string | null =>
    state.obj.error

export default objSlice.reducer
